import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  errorTitle?: string;
  errorDescription?: string;
  tryAgainText?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const {
        errorTitle = "Something went wrong",
        errorDescription = "An unexpected error occurred. Please try again.",
        tryAgainText = "Try again"
      } = this.props;

      return (
        <div className={`${styles.overlay} cl-configurator`}>
          <div
            className={styles.content}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="error-boundary-title"
            aria-describedby="error-boundary-description"
          >
            <h2 id="error-boundary-title" className={styles.title}>
              {errorTitle}
            </h2>

            <p
              id="error-boundary-description"
              className={styles.description}
            >
              {errorDescription}
            </p>

            <button
              type="button"
              className={styles.button}
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              {tryAgainText}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}